import get from 'lodash/get';
import { stores } from '@stores/.';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { IDestDefinition } from '@stores/destinationDefsList';
import sourceFactory from '@components/sources/sourceFactory';

interface Config {
  limit?: number | null;
  count?: number;
}

type ResourceDefinition = Pick<
  ISourceDefinition | IDestDefinition,
  'options' | 'name' | 'category'
>;

export const hasExhaustedLimit = ({ limit, count }: Config) => {
  if (!limit && limit !== 0) {
    return false;
  }
  if (limit < 1) {
    return true;
  }
  if (!count) {
    return false;
  }
  return count >= limit;
};

export const shouldHideResource = (resourceDef: ResourceDefinition) => {
  const isSourceCreateAllowed = sourceFactory(resourceDef.category).isCreateAllowed(
    resourceDef.name,
    stores.featuresStore,
  );

  if (!isSourceCreateAllowed) {
    return true;
  }

  const { options } = resourceDef;

  if (typeof options?.hidden === 'object' && options?.hidden?.featureFlagName) {
    const availableFeatureFlags = stores.featuresStore.getCombinedFeatureFlagsList();
    return (
      get(availableFeatureFlags, options.hidden.featureFlagName) === options.hidden.featureFlagValue
    );
  }
  return !!options?.hidden;
};

export const customMap = <T extends ResourceDefinition>(all: T[]) =>
  all.filter(
    (x) => !shouldHideResource(x) || stores.workspaceStore.enabledSources.includes(x.name),
  );

export const getIntegrationStatus = (icon: {
  isBeta: boolean;
  isDeprecated: boolean;
  isNew?: boolean;
  isLegacy?: boolean;
}) => {
  if (icon.isDeprecated) {
    return 'deprecated' as const;
  }

  if (icon.isLegacy) {
    return 'legacy' as const;
  }

  if (icon.isBeta) {
    return 'beta' as const;
  }

  if (icon.isNew) {
    return 'new' as const;
  }

  return undefined;
};

export const getExtractLimitMessages = (count: number, limit: number) => {
  if (limit === 0) {
    return {
      title: 'Cloud Extract is not available on your current plan.',
      description: 'Upgrade your plan to unlock unlimited cloud extract sources.',
      search: 'Cloud Extract is not available on your current plan.',
      isLimitReached: true,
    };
  }
  if (count === 0 || !limit) {
    return null;
  }
  return {
    title: count > limit ? '' : `${count} of ${limit} Cloud Extract sources created`,
    isLimitReached: count >= limit,
    description:
      count < limit
        ? `You have ${
            limit - count
          } sources remaining for active cloud extract sources. Upgrade your plan to unlock unlimited cloud extract sources.`
        : 'You’ve reached your limit for active cloud extract sources. Upgrade your plan to unlock unlimited cloud extract sources.',
    search: count >= limit ? 'You have reached your limit' : '',
  };
};

export const getWHActionLimitMessages = (count: number, limit: number) => {
  if (limit === 0) {
    return {
      title: 'Reverse ETL is not available on your current plan.',
      description: 'Upgrade your plan to unlock unlimited reverse ETL sources.',
      search: 'Not available on your current plan.',
      isLimitReached: true,
    };
  }
  if (!limit) {
    return null;
  }
  return {
    title: count > limit ? '' : `${count} of ${limit} reverse ETL source connected`,
    isLimitReached: count >= limit,
    description:
      count < limit
        ? `You have ${
            limit - count
          } sources remaining for active reverse ETL sources. Upgrade your plan to unlock unlimited reverse ETL sources.`
        : `You’ve reached your limit of ${limit} rETL connection${
            limit === 1 ? '' : 's'
          }. Upgrade your plan to unlock unlimited rETL connections.`,
    search:
      count >= limit
        ? `You've reached your limit of ${limit} rETL connection${limit === 1 ? '' : 's'}.`
        : '',
  };
};
